import { Mail, MonitorSmartphone } from 'lucide-react';
import clsx from 'clsx';

import { AppDialog } from '@/shared/components/ui/AppDialog';
import type { NotificationPreference } from '@/shared/types/entities';

import { useNotificationPreferences } from '../../api/notifications-queries';
import { useUpdateNotificationPreference } from '../../api/notifications-mutations';
import { NOTIFICATION_PREFERENCE_TYPES } from '../../types/notifications.types';
import styles from './NotificationPreferencesDialog.module.scss';

export interface NotificationPreferencesDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

/** No stored row means the backend hasn't been told otherwise — both channels deliver. */
const DEFAULT_CHANNELS = { emailEnabled: true, inAppEnabled: true };

function getChannels(preferences: NotificationPreference[] | undefined, type: string) {
  const stored = preferences?.find((preference) => preference.type === type);
  return stored ?? DEFAULT_CHANNELS;
}

export function NotificationPreferencesDialog({
  open,
  onOpenChange,
}: NotificationPreferencesDialogProps) {
  // Only fetch once the dialog has been opened.
  const { data: preferences, isLoading, isError } = useNotificationPreferences(open);
  const updateMutation = useUpdateNotificationPreference();

  const toggle = (type: string, channel: 'emailEnabled' | 'inAppEnabled') => {
    const channels = getChannels(preferences, type);
    updateMutation.mutate({
      type,
      emailEnabled: channels.emailEnabled,
      inAppEnabled: channels.inAppEnabled,
      [channel]: !channels[channel],
    });
  };

  return (
    <AppDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Notification preferences"
      description="Choose how you hear about each kind of update. Changes save as you toggle."
      className={styles.dialog}
    >
      {isLoading && (
        <div className={styles.loading}>
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className={styles.skeleton} />
          ))}
        </div>
      )}

      {isError && (
        <p className={styles.error} role="alert">
          Could not load your preferences. Close this and try again.
        </p>
      )}

      {!isLoading && !isError && (
        <div className={styles.table}>
          <div className={styles.headerRow} aria-hidden="true">
            <span />
            <span className={styles.channelHeader}>
              <Mail size={14} />
              Email
            </span>
            <span className={styles.channelHeader}>
              <MonitorSmartphone size={14} />
              In-app
            </span>
          </div>

          {NOTIFICATION_PREFERENCE_TYPES.map(({ type, label, description }) => {
            const channels = getChannels(preferences, type);

            return (
              <div key={type} className={styles.row}>
                <div className={styles.info}>
                  <span className={styles.label}>{label}</span>
                  <span className={styles.description}>{description}</span>
                </div>
                <ToggleSwitch
                  checked={channels.emailEnabled}
                  label={`Email notifications for ${label}`}
                  onChange={() => toggle(type, 'emailEnabled')}
                />
                <ToggleSwitch
                  checked={channels.inAppEnabled}
                  label={`In-app notifications for ${label}`}
                  onChange={() => toggle(type, 'inAppEnabled')}
                />
              </div>
            );
          })}
        </div>
      )}
    </AppDialog>
  );
}

function ToggleSwitch({
  checked,
  label,
  onChange,
}: {
  checked: boolean;
  label: string;
  onChange: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={onChange}
      className={clsx(styles.switch, checked && styles.switchOn)}
    >
      <span className={styles.thumb} />
    </button>
  );
}
